import { useState } from 'react';
import { AlertCircle, Tag, BookOpen } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import PageHeader from '@/components/shared/PageHeader';
import { usePageTitle } from '@/hooks/usePageTitle';
import BrandsPanel from './BrandsPanel';
import CataloguesPanel from './CataloguesPanel';

// Brand & catalogue admin for the Trade Portal. Two tabs share a single
// error/notice banner so feedback is consistent across both panels.
export default function BrandManagement() {
  usePageTitle('Brands & Catalogues');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const showError = (msg: string) => { setNotice(null); setError(msg); };
  const showNotice = (msg: string) => { setError(null); setNotice(msg); };

  return (
    <div className="w-full space-y-4">
      <PageHeader
        title="Brands & Catalogues"
        subtitle="Manage the canonical brands and PDF catalogues shown across the Trade Portal."
      />

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" /> {error}
        </div>
      )}
      {notice && !error && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400">
          {notice}
        </div>
      )}

      <Tabs defaultValue="brands" className="w-full">
        <TabsList>
          <TabsTrigger value="brands" className="gap-1.5"><Tag className="h-4 w-4" /> Brands</TabsTrigger>
          <TabsTrigger value="catalogues" className="gap-1.5"><BookOpen className="h-4 w-4" /> Catalogues</TabsTrigger>
        </TabsList>

        <TabsContent value="brands" className="mt-4">
          <BrandsPanel onError={showError} onNotice={showNotice} />
        </TabsContent>

        <TabsContent value="catalogues" className="mt-4">
          <CataloguesPanel onError={showError} onNotice={showNotice} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
